import React, { useId, useState } from 'react';
import { ChevronRightIcon } from '../icons';

interface CollapsibleCardProps {
  icon: React.ReactNode;
  title: React.ReactNode;
  badges?: React.ReactNode;
  // Rendered beside the toggle, not inside it, so buttons are never nested.
  actions?: React.ReactNode;
  // Shown only while collapsed, e.g. source chips.
  preview?: React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  children: React.ReactNode;
}

const CollapsibleCard: React.FC<CollapsibleCardProps> = ({
  icon, title, badges, actions, preview, defaultExpanded = false, expanded, onExpandedChange, children,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = expanded ?? internalExpanded;
  const bodyId = useId();

  const toggle = () => {
    setInternalExpanded(!isExpanded);
    onExpandedChange?.(!isExpanded);
  };

  return (
    <div className="cv-card">
      <div className="cv-card__header">
        <button
          type="button"
          className="cv-card__toggle"
          aria-expanded={isExpanded}
          aria-controls={bodyId}
          onClick={toggle}
        >
          <span className="cv-card__icon">{icon}</span>
          <span className="cv-card__title">{title}</span>
          {badges}
          <span className="cv-card__chevron"><ChevronRightIcon size={16} /></span>
        </button>
        {actions && <div className="cv-card__actions">{actions}</div>}
      </div>

      {!isExpanded && preview && <div className="cv-card__preview">{preview}</div>}

      {isExpanded && <div id={bodyId} className="cv-card__body">{children}</div>}
    </div>
  );
};

export default CollapsibleCard;
